/**
 * Customer cart: lists orders that are awaiting payment and starts a Stripe
 * Checkout session for the selected ones.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { BanknotesIcon, ShoppingCartIcon } from '@heroicons/react/24/solid';
import { useAuth, supabase } from '../context/AuthContext';
import CustomerLayout from './CustomerLayout';
import PaymentSuccessModal from './PaymentSuccessModal';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const ITEMS_PER_PAGE = 50;

const Cart = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [cartItems, setCartItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cartRefreshKey, setCartRefreshKey] = useState(0);

  // Stripe redirects back to /cart?session_id=… after checkout; it opens the success modal on cart itself
  const paymentSessionId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('session_id');
  }, [location.search]);

  // Pending cart rows come from the denormalized view (no nested joins).
  // payment_status filter keeps unpaid invoices only — same set as the old !inner join.
  useEffect(() => {
    const fetchCartItems = async () => {
      try {
        if (!user?.id) {
          setLoading(false);
          return;
        }

        const { data, error } = await supabase
          .from('admin_submissions_view')
          .select(`
            id,
            vin_number,
            car_make,
            car_model,
            mr_approval,
            transaction_id,
            transaction_price,
            invoice_number,
            created_at
          `)
          .eq('user_id', user.id)
          .eq('payment_status', 'Payment Pending')
          .eq('is_archived', false)
          .order('created_at', { ascending: false });

        if (error) throw error;

        setCartItems(data || []);
        setError(null);
        window.dispatchEvent(new Event('customer-cart-updated'));
      } catch (error) {
        console.error('Error fetching cart items:', error);
        setError('Failed to load cart items. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchCartItems();
  }, [user, cartRefreshKey]);

  // Handle pre-selected order when component mounts
  useEffect(() => {
    if (location.state?.preSelectedOrderId && cartItems.length > 0) {
      const preSelectedId = location.state.preSelectedOrderId;
      setSelectedItems(new Set([preSelectedId]));
      const itemIndex = cartItems.findIndex((item) => item.id === preSelectedId);
      if (itemIndex >= 0) {
        setCurrentPage(Math.floor(itemIndex / ITEMS_PER_PAGE) + 1);
      }
      // Clear the state after using it to prevent re-selection on page refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state, cartItems]);

  const totalPages = Math.max(1, Math.ceil(cartItems.length / ITEMS_PER_PAGE));

  // Paid items leave the cart, so the current page can end up past the last page
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return cartItems.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [cartItems, currentPage]);

  // price lookups from view columns
  const priceByItemId = useMemo(() => {
    const map = new Map();
    cartItems.forEach((item) => {
      map.set(item.id, parseFloat(item.transaction_price) || 0);
    });
    return map;
  }, [cartItems]);

  const allSelected = cartItems.length > 0 && selectedItems.size === cartItems.length;
  const someSelected = selectedItems.size > 0 && !allSelected;

  const selectedTotal = useMemo(() => {
    let total = 0;
    selectedItems.forEach((id) => {
      total += priceByItemId.get(id) || 0;
    });
    return total;
  }, [selectedItems, priceByItemId]);

  // Only materialize selected item details for the summary when not "select all"
  const selectedCartItems = useMemo(() => {
    if (allSelected || selectedItems.size === 0) return [];
    return cartItems.filter((item) => selectedItems.has(item.id));
  }, [allSelected, selectedItems, cartItems]);

  const getItemPrice = (item) => priceByItemId.get(item.id) || 0;

  // Strips session_id from the URL so the modal cannot reopen on page refresh
  const handlePaymentModalClose = () => {
    navigate('/cart', { replace: true });
    setSelectedItems(new Set());
    setCartRefreshKey((current) => current + 1);
  };

  // Re-reads selected rows from the view for fresh prices, then starts Stripe Checkout
  const initiateCheckout = async () => {
    if (selectedItems.size === 0) return;
    try {
      const selectedMRIds = Array.from(selectedItems);
      const stripe = await stripePromise;

      if (!stripe) {
        throw new Error('Stripe failed to initialize');
      }

      // Chunk .in() queries — PostgREST URL limits break with very large ID lists
      const chunkSize = 50;
      const selectedRows = [];
      for (let i = 0; i < selectedMRIds.length; i += chunkSize) {
        const chunk = selectedMRIds.slice(i, i + chunkSize);
        const { data: chunkData, error: fetchError } = await supabase
          .from('admin_submissions_view')
          .select('id, transaction_id, transaction_price')
          .eq('user_id', user.id)
          .eq('payment_status', 'Payment Pending')
          .eq('is_archived', false)
          .in('id', chunk);

        if (fetchError) {
          console.error('Error fetching checkout rows:', fetchError);
          throw fetchError;
        }
        if (chunkData?.length) {
          selectedRows.push(...chunkData);
        }
      }

      if (selectedRows.length !== selectedMRIds.length) {
        throw new Error('Some selected orders were archived or removed. Refresh your cart and try again.');
      }

      const selectedTransactions = selectedRows.filter((row) => row.transaction_id);
      if (selectedTransactions.length === 0) {
        throw new Error('Selected orders are no longer available for payment. Refresh your cart and try again.');
      }

      // Charge the prices just read from the DB rather than the rendered cart totals
      const transactionIds = selectedTransactions.map((row) => row.transaction_id);
      const totalAmount = selectedTransactions.reduce(
        (sum, row) => sum + (Number(row.transaction_price) || 0),
        0
      );

      // Transaction and submission ids travel with the session so the Stripe webhook can
      // mark the right rows as Paid once payment completes
      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-payment-intent`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            amount: totalAmount,
            transaction_ids: transactionIds,
            submission_ids: selectedTransactions.map((row) => row.id),
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Server error:', errorData);
        throw new Error(errorData || 'Failed to create payment session');
      }

      const data = await response.json();
      const { error: stripeError } = await stripe.redirectToCheckout({
        sessionId: data.sessionId,
      });

      if (stripeError) {
        console.error('Stripe redirect error:', stripeError);
        throw stripeError;
      }

    } catch (error) {
      console.error('Error during checkout:', error);
      setError('Failed to process checkout. Please try again.');
    }
  };

  const handleItemSelect = (itemId) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(itemId)) {
        newSelected.delete(itemId);
      } else {
        newSelected.add(itemId);
      }
      return newSelected;
    });
  };

  // Select / clear ALL cart items across pages (not just the current page)
  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(cartItems.map((item) => item.id)));
    }
  };

  // Sliding window of at most 5 page buttons, kept centred on the current page
  const pageNumbers = useMemo(() => {
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    const numbers = [];
    for (let i = startPage; i <= endPage; i++) {
      numbers.push(i);
    }
    return { numbers, startPage, endPage };
  }, [currentPage, totalPages]);

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Please log in to view your cart.</p>
          <button
            onClick={() => navigate('/login')}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-500"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading cart items...</p>
        </div>
      </div>
    );
  }

  return (
    <CustomerLayout
      activeItemId="cart"
      title="My Cart"
      subtitle="Review your orders and proceed to checkout."
      contentClassName="mx-auto w-full"
      footerContainerClassName="mt-16 lg:mt-24"
    >
      <div className="grid items-start gap-6 pb-10 xl:grid-cols-[minmax(0,1.7fr)_minmax(320px,0.9fr)] xl:pb-16">
        <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm shadow-slate-200/60">
          <div className="flex items-center justify-between px-5 pt-5 pb-3 sm:px-6">
            <div>
              <h2 className="flex items-center gap-2 text-md font-medium text-slate-900 uppercase tracking-[0.04em]">
                <ShoppingCartIcon className="h-5 w-5 text-blue-600" />
                Review Your Items
              </h2>
              <p className="mt-1 hidden text-sm text-slate-500 sm:block">
                Select the orders you want to include in checkout.
              </p>
            </div>
            <span className="hidden text-sm text-slate-500 sm:inline">
              {cartItems.length} items
              {selectedItems.size > 0 && (
                <span className="text-blue-600"> · {selectedItems.size} selected</span>
              )}
            </span>
          </div>
          <div className="border-t border-slate-200">
            <div className="flex items-center gap-3 border-b border-slate-200 px-5 py-3 sm:px-6">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected;
                }}
                onChange={handleSelectAll}
                className="h-4 w-4 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                aria-label="Select all items"
              />
              <span className="text-sm font-medium text-slate-700">Select all</span>
            </div>
            {cartItems.length === 0 ? (
              <div className="px-5 py-8 text-center sm:px-6">
                <p className="text-slate-500">Your cart is empty</p>
                <button
                  onClick={() => navigate('/new-order')}
                  className="mt-4 inline-flex items-center justify-center rounded-[0.9rem] bg-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-all duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Start New Order
                </button>
              </div>
            ) : (
              <>
                <ul className="divide-y divide-slate-200">
                  {paginatedItems.map((item) => (
                    <li key={item.id} className="px-5 py-4 sm:px-6">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedItems.has(item.id)}
                          onChange={() => handleItemSelect(item.id)}
                          className="mr-4 h-4 w-4 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium text-blue-600">
                                {item.car_make} {item.car_model}
                              </p>
                              <div className="mt-1 space-y-1">
                                <p className="text-sm">
                                  <span className="text-slate-500">VIN:</span>{' '}
                                  <span className="text-slate-700">{item.vin_number}</span>
                                </p>
                                <p className="text-sm">
                                  <span className="text-slate-500">MRE Approval:</span>{' '}
                                  <span className="text-slate-700">{item.mr_approval || '- -'}</span>
                                </p>
                                <p className="text-sm">
                                  <span className="text-slate-500">Invoice Number:</span>{' '}
                                  <span className="text-slate-700">{item.invoice_number || '- -'}</span>
                                </p>
                              </div>
                            </div>
                            <div className="ml-4 flex-shrink-0">
                              <p className="text-sm font-medium text-slate-900">
                                ${getItemPrice(item).toFixed(2)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>

                {totalPages > 1 && (
                  <div className="flex items-center justify-between border-t border-slate-200 bg-white px-5 py-3 sm:px-6">
                    <p className="text-sm text-slate-700">
                      Showing{' '}
                      <span className="font-medium">
                        {(currentPage - 1) * ITEMS_PER_PAGE + 1}
                      </span>{' '}
                      to{' '}
                      <span className="font-medium">
                        {Math.min(currentPage * ITEMS_PER_PAGE, cartItems.length)}
                      </span>{' '}
                      of <span className="font-medium">{cartItems.length}</span>
                    </p>
                    <nav className="inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                      <button
                        type="button"
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center rounded-l-[0.75rem] border border-slate-300 bg-white px-2 py-2 text-slate-400 hover:bg-slate-50 disabled:opacity-50"
                      >
                        <span className="sr-only">Previous</span>
                        <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                      </button>

                      {pageNumbers.startPage > 1 && (
                        <>
                          <button
                            type="button"
                            onClick={() => setCurrentPage(1)}
                            className="relative inline-flex items-center border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                          >
                            1
                          </button>
                          {pageNumbers.startPage > 2 && (
                            <span className="relative inline-flex items-center border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700">
                              ...
                            </span>
                          )}
                        </>
                      )}

                      {pageNumbers.numbers.map((number) => (
                        <button
                          key={number}
                          type="button"
                          onClick={() => setCurrentPage(number)}
                          className={`relative inline-flex items-center border px-4 py-2 text-sm font-medium ${
                            currentPage === number
                              ? 'z-10 border-blue-500 bg-blue-50 text-blue-600'
                              : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                          }`}
                        >
                          {number}
                        </button>
                      ))}

                      {pageNumbers.endPage < totalPages && (
                        <>
                          {pageNumbers.endPage < totalPages - 1 && (
                            <span className="relative inline-flex items-center border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700">
                              ...
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => setCurrentPage(totalPages)}
                            className="relative inline-flex items-center border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                          >
                            {totalPages}
                          </button>
                        </>
                      )}

                      <button
                        type="button"
                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center rounded-r-[0.75rem] border border-slate-300 bg-white px-2 py-2 text-slate-400 hover:bg-slate-50 disabled:opacity-50"
                      >
                        <span className="sr-only">Next</span>
                        <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </nav>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        <aside className="self-start overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm shadow-slate-200/60">
          <div className="px-5 py-5 sm:px-6 sm:py-6">
            <h2 className="mb-4 flex items-center gap-2 text-md font-medium text-slate-900 uppercase tracking-[0.04em]">
              <BanknotesIcon className="h-5 w-5 text-blue-600" />
              Summary
            </h2>

            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-[1rem] border border-slate-200 bg-slate-50/80 px-4 py-3 shadow-sm">
                <span className="text-sm text-slate-600">Selected Items</span>
                <span className="text-sm font-medium text-slate-900">{selectedItems.size}</span>
              </div>

              <div className="border-t border-slate-200 pt-4">
                <h3 className="mb-2 text-sm font-medium uppercase tracking-[0.04em] text-slate-900">Selected Orders</h3>
                {selectedItems.size === 0 ? (
                  <p className="text-sm text-slate-500">No items selected</p>
                ) : allSelected ? (
                  <div className="rounded-[1rem] border border-blue-100 bg-blue-50 px-4 py-3 shadow-sm">
                    <p className="text-sm font-medium text-blue-800">
                      All {cartItems.length} items selected
                    </p>
                    <p className="mt-1 text-xs text-blue-600">
                      Checkout will include every pending order in your cart.
                    </p>
                  </div>
                ) : (
                  <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
                    {selectedCartItems.map((item) => (
                      <div key={item.id} className="rounded-[1rem] border border-slate-200 bg-slate-50/80 px-4 py-3 shadow-sm">
                        <div className="text-sm">
                          <p className="font-medium text-blue-600">
                            {item.car_make} {item.car_model}
                          </p>
                          <div className="mt-1 space-y-1">
                            <p>
                              <span className="text-slate-500">VIN:</span>{' '}
                              <span className="text-slate-700">{item.vin_number}</span>
                            </p>
                            <p>
                              <span className="text-slate-500">MRE Approval:</span>{' '}
                              <span className="text-slate-700">{item.mr_approval || '- -'}</span>
                            </p>
                            <p>
                              <span className="text-slate-500">Invoice Number:</span>{' '}
                              <span className="text-slate-700">{item.invoice_number || '- -'}</span>
                            </p>
                          </div>
                          <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-2">
                            <span className="text-slate-500">Price:</span>
                            <span className="font-medium text-slate-900">${getItemPrice(item).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-t border-slate-200 pt-4">
                <div className="flex items-center justify-between">
                  <span className="text-base font-medium text-slate-900">Total Amount</span>
                  <span className="text-xl font-bold text-blue-600">
                    ${selectedTotal.toFixed(2)}
                  </span>
                </div>
              </div>

              <button
                onClick={initiateCheckout}
                disabled={selectedItems.size === 0}
                className={`mt-2 inline-flex w-full items-center justify-center rounded-[0.9rem] px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  selectedItems.size > 0
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'cursor-not-allowed bg-slate-300'
                }`}
              >
                Proceed to Checkout
              </button>
            </div>
          </div>
        </aside>
      </div>
      <PaymentSuccessModal
        sessionId={paymentSessionId}
        onClose={handlePaymentModalClose}
      />
    </CustomerLayout>
  );
};

export default Cart; 
